<script setup>
    import { computed } from 'vue';

    const props = defineProps({
        cuotas: {
            type: Array,
            required: true
        },
        nCuotas: {
            type: Number,
            required: true
        },
        breakdown: {
            type: Object,
            required: true
        },
        numAbonados: {
            type: Number,
            default: 1
        },
        loading: {
            type: Boolean,
            default: false
        }
    });

    const emit = defineEmits(['select', 'pay']);

    const formatPrice = value => {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2
        }).format(value || 0);
    };

    // Cuota option: per-cuota total = (base + feePerCuota * n * numAbonados) / n
    // The fee is per-abonado and the cart base already includes all abonados,
    // so the fee side needs the numAbonados factor to stay consistent with
    // `breakdown.feeSubtotal` and `breakdown.total`.
    const optionsWithPrice = computed(() => {
        const base = props.breakdown.base;
        const feePerCuota = props.breakdown.feePerCuota;
        const numAbonados = props.numAbonados;
        return props.cuotas.map(n => {
            const total = base + (feePerCuota * n * numAbonados);
            return {
                n,
                total,
                perMonth: total / n,
                feePerCuota
            };
        });
    });

    const onSelect = n => {
        if (props.loading) return;
        emit('select', n);
    };

    const onPay = () => {
        if (props.loading) return;
        emit('pay');
    };
</script>

<template>
    <!--
      Shell-less on purpose: the outer card border and the trust strip live in
      PaymentStep.vue so the whole payment step reads as a single card. The
      individual cuota buttons stay bordered because each one is an actionable
      selector the user needs to differentiate at a glance.
    -->
    <div data-test="instant-credit-option" class="tw:space-y-5">
        <!-- Header: eyebrow + title + subtitle -->
        <div class="tw:pt-1">
            <div class="tw:flex tw:items-center tw:gap-2 tw:mb-4">
                <span
                    data-test="ic-eyebrow"
                    class="tw:inline-flex tw:items-center tw:gap-1.5 tw:px-2.5 tw:py-1 tw:bg-alt/15 tw:text-[#8a7d3f] tw:font-bold tw:tracking-[0.18em] tw:text-[10px] tw:uppercase tw:rounded-full tw:border tw:border-alt/40"
                >
                    <span class="tw:w-1.5 tw:h-1.5 tw:bg-alt tw:rounded-full"></span>
                    Instant Credit
                </span>
            </div>
            <h3
                data-test="ic-title"
                class="tw:text-2xl tw:font-bold tw:uppercase tw:text-site tw:leading-[1.15] tw:mb-2 tw:tracking-tight"
            >
                {{ $t('instant_credit.titulo') }}
            </h3>
            <p class="tw:text-sm tw:text-site/60 tw:leading-relaxed">
                {{ $t('instant_credit.subtitle') }}
                <span class="tw:text-site/40">·</span>
                <span class="tw:font-semibold tw:text-site/70"> {{ $t('instant_credit.partner') }}</span>
            </p>
        </div>

        <!-- Cuotas selector: horizontal grid, premium cards -->
        <div
            data-test="instant-credit-cuotas"
            class="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:md:grid-cols-3 tw:gap-3"
        >
            <button
                v-for="opt in optionsWithPrice"
                :key="opt.n"
                type="button"
                :data-test="`instant-credit-cuota-${opt.n}`"
                :aria-pressed="nCuotas === opt.n"
                :aria-label="$t('instant_credit.cuotas_label', { n: opt.n })"
                :disabled="loading"
                class="tw:relative tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-5 tw:px-3 tw:rounded-xl tw:border-2 tw:transition-all tw:cursor-pointer tw:group focus:tw:outline-none focus-visible:tw:ring-2 focus-visible:tw:ring-alt focus-visible:tw:ring-offset-2 disabled:tw:opacity-60 disabled:tw:cursor-not-allowed"
                :class="nCuotas === opt.n
                    ? 'tw:border-alt tw:bg-alt tw:text-site tw:shadow-[0_4px_12px_-2px_rgba(209,198,125,0.4)]'
                    : 'tw:border-site/15 tw:bg-white tw:text-site hover:tw:border-site/40 hover:tw:-translate-y-0.5'"
                @click="onSelect(opt.n)"
            >
                <!-- Check icon (selected state) -->
                <span
                    v-if="nCuotas === opt.n"
                    class="tw:absolute tw:top-2.5 tw:right-2.5 tw:w-5 tw:h-5 tw:bg-site tw:text-alt tw:rounded-full tw:flex tw:items-center tw:justify-center tw:shadow-sm"
                >
                    <svg
                        class="tw:w-3 tw:h-3"
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path d="M2.5 6.5l2.5 2.5 4.5-5" />
                    </svg>
                </span>

                <!-- Big number -->
                <span
                    class="tw:text-5xl tw:font-bold tw:leading-none tw:mb-1 tw:tabular-nums tw:tracking-tight"
                >
                    {{ opt.n }}
                </span>

                <!-- "cuotas" small label -->
                <span
                    class="tw:text-[10px] tw:font-bold tw:uppercase tw:tracking-[0.2em] tw:opacity-60 tw:mb-4"
                >
                    {{ $t('instant_credit.cuotas_word') }}
                </span>

                <!-- Price per month -->
                <span
                    class="tw:text-base tw:font-bold tw:tabular-nums tw:leading-none tw:mb-1"
                    :data-test="`instant-credit-per-month-${opt.n}`"
                >
                    {{ formatPrice(opt.perMonth) }}
                </span>
                <span
                    class="tw:text-[10px] tw:opacity-60 tw:uppercase tw:tracking-[0.15em] tw:font-semibold"
                >
                    {{ $t('instant_credit.per_month') }}
                </span>
            </button>
        </div>

        <!-- Breakdown -->
        <div class="tw:space-y-1.5 tw:py-3 tw:text-sm tw:border-t tw:border-site/10">
            <div class="tw:flex tw:justify-between tw:text-site/70 tw:items-baseline">
                <span>{{ $t('instant_credit.base_price') }}</span>
                <span class="tw:tabular-nums tw:font-medium">{{ formatPrice(breakdown.base) }}</span>
            </div>
            <div class="tw:flex tw:justify-between tw:text-site/70 tw:items-baseline">
                <span>
                    {{ $t('instant_credit.fee_per_cuota') }}
                    <span class="tw:opacity-60">
                        ({{ nCuotas }} × {{ formatPrice(breakdown.feePerCuota) }}{{ numAbonados > 1 ? ` × ${numAbonados} ab.` : '' }})
                    </span>
                </span>
                <span class="tw:tabular-nums tw:font-medium">{{ formatPrice(breakdown.feeSubtotal) }}</span>
            </div>
        </div>
        <div
            class="tw:flex tw:justify-between tw:items-baseline tw:pt-3 tw:border-t-2 tw:border-site"
        >
            <span
                class="tw:text-xs tw:font-bold tw:uppercase tw:tracking-[0.2em] tw:text-site"
            >
                {{ $t('instant_credit.total') }}
            </span>
            <span
                data-test="instant-credit-total"
                class="tw:text-2xl tw:font-bold tw:text-site tw:tabular-nums tw:tracking-tight"
            >
                {{ formatPrice(breakdown.total) }}
            </span>
        </div>

        <!-- CTA: full-width, primary, matches the rest of the payment step -->
        <div>
            <button
                type="button"
                data-test="instant-credit-pay"
                class="tw:w-full tw:py-3 tw:bg-site tw:text-white tw:font-bold tw:uppercase tw:tracking-[0.15em] tw:rounded-md tw:transition-colors hover:tw:bg-black focus:tw:outline-none focus-visible:tw:ring-2 focus-visible:tw:ring-site focus-visible:tw:ring-offset-2 disabled:tw:opacity-50 disabled:tw:cursor-not-allowed tw:flex tw:items-center tw:justify-center tw:gap-2.5 tw:text-sm"
                :disabled="loading"
                @click="onPay"
            >
                <span
                    v-if="loading"
                    class="tw:inline-block tw:w-4 tw:h-4 tw:border-2 tw:border-white/30 tw:border-t-white tw:rounded-full tw:animate-spin"
                ></span>
                <span>{{ loading ? $t('instant_credit.processing') : $t('instant_credit.pay_button') }}</span>
                <svg
                    v-if="!loading"
                    class="tw:w-4 tw:h-4 tw:opacity-80"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
            </button>
        </div>
    </div>
</template>
